import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Shield, 
  Users, 
  FileText, 
  DollarSign, 
  TrendingUp,
  LogOut,
  Search,
  ChevronRight,
  Loader2,
  CreditCard,
  UserPlus,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const token = sessionStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, []);

  const getAdminHeader = () => ({
    Authorization: `Bearer ${sessionStorage.getItem('admin_token')}`
  });

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard`, { headers: getAdminHeader() }),
        axios.get(`${API}/admin/users`, { headers: getAdminHeader() }),
        axios.get(`${API}/admin/transactions`, { headers: getAdminHeader() })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      // Backend returns {transactions: [...]} so extract the array
      setTransactions(transactionsRes.data.transactions || []);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        sessionStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    sessionStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle, label: 'Active' },
      trialing: { color: 'bg-blue-100 text-blue-700', icon: Clock, label: 'Trial' },
      past_due: { color: 'bg-amber-100 text-amber-700', icon: AlertCircle, label: 'Past Due' },
      cancelled: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Cancelled' },
      none: { color: 'bg-slate-100 text-slate-600', icon: XCircle, label: 'No Plan' }
    };
    const { color, icon: Icon, label } = config[status] || config.none;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${color}`}>
        <Icon className="w-3 h-3" />
        {label}
      </span>
    );
  };

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.firm_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.full_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-teal-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                IntakePal Admin
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                className="text-slate-400 hover:text-white hover:bg-slate-700"
                onClick={() => navigate('/admin/system-health')}
              >
                <Activity className="w-4 h-4 mr-2" />
                System Health
              </Button>
              <Button 
                variant="ghost" 
                className="text-slate-400 hover:text-white hover:bg-slate-700"
                onClick={handleLogout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-400">Total Users</CardTitle>
              <Users className="h-5 w-5 text-teal-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{stats?.total_users || 0}</div>
              <p className="text-xs text-slate-500 mt-1">
                +{stats?.recent_signups || 0} this week
              </p>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-400">Active Subs</CardTitle>
              <CreditCard className="h-5 w-5 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-emerald-400">{stats?.active_subscriptions || 0}</div>
              <p className="text-xs text-slate-500 mt-1">
                {stats?.trial_users || 0} in trial
              </p>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-400">Total Leads</CardTitle>
              <FileText className="h-5 w-5 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{stats?.total_leads || 0}</div>
              <p className="text-xs text-slate-500 mt-1">
                +{stats?.recent_leads || 0} this week
              </p>
            </CardContent>
          </Card>

          <Card className="bg-slate-800 border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-400">Revenue</CardTitle>
              <DollarSign className="h-5 w-5 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-amber-400">
                {formatCurrency(stats?.estimated_revenue || 0)}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {stats?.transactions_count || 0} transactions
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-slate-700 pb-4">
          <Button
            variant={activeTab === 'overview' ? 'default' : 'ghost'}
            className={activeTab === 'overview' ? 'bg-teal-500' : 'text-slate-400'}
            onClick={() => setActiveTab('overview')}
          >
            <Users className="w-4 h-4 mr-2" />
            Users
          </Button>
          <Button
            variant={activeTab === 'transactions' ? 'default' : 'ghost'}
            className={activeTab === 'transactions' ? 'bg-teal-500' : 'text-slate-400'}
            onClick={() => setActiveTab('transactions')}
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Transactions
          </Button>
        </div>

        {/* Users Tab */}
        {activeTab === 'overview' && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  All Users
                </CardTitle>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                    data-testid="admin-search-users"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border border-slate-700 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-900 border-slate-700">
                      <TableHead className="text-slate-400">User</TableHead>
                      <TableHead className="text-slate-400">Firm</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                      <TableHead className="text-slate-400">Leads</TableHead>
                      <TableHead className="text-slate-400">Joined</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((user) => (
                      <TableRow 
                        key={user.id} 
                        className="border-slate-700 hover:bg-slate-700/50 cursor-pointer"
                        onClick={() => navigate(`/admin/users/${user.id}`)}
                      >
                        <TableCell>
                          <div>
                            <p className="font-medium text-white">{user.full_name}</p>
                            <p className="text-sm text-slate-400">{user.email}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-slate-300">{user.firm_name}</TableCell>
                        <TableCell>{getStatusBadge(user.subscription_status)}</TableCell>
                        <TableCell className="text-slate-300">{user.leads_count}</TableCell>
                        <TableCell className="text-slate-400">{formatDate(user.created_at)}</TableCell>
                        <TableCell>
                          <ChevronRight className="w-4 h-4 text-slate-500" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {filteredUsers.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    No users found
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Payment Transactions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-lg border border-slate-700 overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-900 border-slate-700">
                      <TableHead className="text-slate-400">User</TableHead>
                      <TableHead className="text-slate-400">Amount</TableHead>
                      <TableHead className="text-slate-400">Status</TableHead>
                      <TableHead className="text-slate-400">Plan</TableHead>
                      <TableHead className="text-slate-400">Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map((tx) => (
                      <TableRow key={tx.id} className="border-slate-700">
                        <TableCell className="text-slate-300">{tx.user_email}</TableCell>
                        <TableCell className="text-white font-medium">
                          {formatCurrency(tx.amount)}
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            tx.payment_status === 'paid' 
                              ? 'bg-emerald-100 text-emerald-700'
                              : tx.payment_status === 'pending'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {tx.payment_status}
                          </span>
                        </TableCell>
                        <TableCell className="text-slate-300 capitalize">{tx.plan}</TableCell>
                        <TableCell className="text-slate-400">{formatDate(tx.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {transactions.length === 0 && (
                  <div className="text-center py-8 text-slate-500">
                    No transactions yet
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default AdminDashboardPage;
